import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import { validateCedulaCR } from "@/common/lib/validateCedulaCR";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLotsList } from "@/features/lots/api/lotsApi";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type InvoiceDocType,
  type IvaRate,
  type RegisterCostInput,
  useRegisterCost,
} from "../api/costAllocationsApi";
import type { CostCategory } from "../utils/categories";
import { firstDayOfCurrentMonth, isoLocalDate } from "../utils/formatPeriod";
import { AmountInput } from "./AmountInput";
import { CategorySelect } from "./CategorySelect";
import { UnusualAmountConfirmDialog } from "./UnusualAmountConfirmDialog";

type Props = {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function RegisterCostDialog({ accountId, open, onOpenChange, onSuccess }: Props) {
  const lotsQuery = useLotsList(accountId);
  const mutation = useRegisterCost(accountId);

  // `today` para los maxDate de los DatePickers — bound visual del calendario.
  // La validación de "no fechas futuras" en el submit usa `new Date()` directo,
  // por si el dialog quedó abierto cruzando medianoche (ver buildInput).
  const today = new Date();
  const [lotId, setLotId] = useState("");
  const [category, setCategory] = useState<CostCategory | "">("");
  const [amount, setAmount] = useState(""); // canonical decimal string
  const [periodStart, setPeriodStart] = useState<Date | undefined>(undefined);
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "credit">("credit");
  const [occurredAt, setOccurredAt] = useState<Date | undefined>(undefined);
  const [description, setDescription] = useState("");
  // B67 (facturas-v1 §7): sección colapsable "Datos de factura (opcional)" —
  // cerrada por default para no enlentecer el flujo rápido de carga.
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(undefined);
  const [docType, setDocType] = useState<InvoiceDocType>("factura_electronica");
  const [ivaAmount, setIvaAmount] = useState(""); // canonical decimal string
  const [ivaRate, setIvaRate] = useState<IvaRate | "">("");
  const [error, setError] = useState<string | null>(null);
  const [unusualMessage, setUnusualMessage] = useState<string | null>(null);
  // Idempotency-Key por mount/open: UUID v4 nuevo cada vez que el dialog se
  // abre (cerrar y reabrir = otra intención de registro). Persiste entre el
  // submit inicial y el confirm-unusual-amount para que el retry comparta key.
  // Ver src/common/idempotency.ts (B23 backend).
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);

  // Reset al abrir.
  useEffect(() => {
    if (!open) return;
    setLotId("");
    setCategory("");
    setAmount("");
    setPeriodStart(firstDayOfCurrentMonth());
    setPeriodEnd(new Date());
    setPaymentMethod("credit");
    setOccurredAt(new Date());
    setDescription("");
    setInvoiceOpen(false);
    setInvoiceNumber("");
    setSupplierName("");
    setSupplierId("");
    setInvoiceDate(undefined);
    setDocType("factura_electronica");
    setIvaAmount("");
    setIvaRate("");
    setError(null);
    setUnusualMessage(null);
    // crypto.randomUUID es nativo en navegadores modernos; produce UUID v4
    // criptográficamente seguro.
    setIdempotencyKey(crypto.randomUUID());
  }, [open]);

  const lots = (lotsQuery.data?.lots ?? []).filter((l) => l.isActive);

  const formIncomplete =
    !lotId ||
    !category ||
    !amount ||
    !periodStart ||
    !periodEnd ||
    !occurredAt ||
    description.trim().length === 0;

  function buildInput(): RegisterCostInput | null {
    if (!lotId || !category || !amount || !periodStart || !periodEnd || !occurredAt) return null;
    if (description.trim().length === 0) return null;

    const startStr = isoLocalDate(periodStart);
    const endStr = isoLocalDate(periodEnd);
    if (endStr < startStr) {
      setError("La fecha de fin debe ser igual o posterior a la de inicio.");
      return null;
    }
    // Capturar today() en el submit, no en render — el dialog puede quedar
    // abierto cruzando medianoche.
    const todayStr = isoLocalDate(new Date());
    if (startStr > todayStr || endStr > todayStr) {
      setError("Las fechas del período no pueden ser futuras.");
      return null;
    }
    if (isoLocalDate(occurredAt) > todayStr) {
      setError("La fecha de registro no puede ser futura.");
      return null;
    }

    // B67: los datos de factura viajan solo si el usuario capturó algo en la
    // sección (doc_type solo acompaña — un default solo no es "dato").
    const hasInvoiceData =
      invoiceNumber.trim() !== "" ||
      supplierName.trim() !== "" ||
      supplierId.trim() !== "" ||
      invoiceDate !== undefined ||
      ivaAmount !== "" ||
      ivaRate !== "";

    if (invoiceDate && isoLocalDate(invoiceDate) > todayStr) {
      setError("La fecha de emisión de la factura no puede ser futura.");
      return null;
    }

    return {
      lotId,
      category,
      amount,
      currencyCode: "CRC",
      periodStart: startStr,
      periodEnd: endStr,
      description: description.trim(),
      paymentMethod,
      occurredAt: occurredAt.toISOString(),
      ...(hasInvoiceData && {
        ...(invoiceNumber.trim() && { invoice_number: invoiceNumber.trim() }),
        ...(supplierName.trim() && { supplier_name: supplierName.trim() }),
        ...(supplierId.trim() && { supplier_id: supplierId.trim() }),
        ...(invoiceDate && { invoice_date: isoLocalDate(invoiceDate) }),
        doc_type: docType,
        ...(ivaAmount && { iva_amount: ivaAmount }),
        ...(ivaRate && { iva_rate: ivaRate }),
      }),
    };
  }

  async function submitWith(confirmUnusual: boolean) {
    // Guard sincrónico contra double-submit. `mutation.isPending` se setea
    // ANTES de que React re-renderize, así que un segundo submit dentro del
    // mismo frame (Enter spam, doble click rápido) ve el flag ya activo y
    // sale temprano. La defensa de UI con `disabled` cubre la mayoría de
    // casos pero hay un frame de gap; este guard lo cierra (D3 idempotencia).
    if (mutation.isPending) return;

    setError(null);
    const input = buildInput();
    if (!input) {
      if (formIncomplete) setError("Completá todos los campos obligatorios.");
      return;
    }

    try {
      await mutation.mutateAsync({
        ...input,
        confirmUnusual,
        idempotencyKey: idempotencyKey ?? undefined,
      });
      toast.success("Costo registrado y prorrateado.");
      setUnusualMessage(null);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError && e.code === "UNUSUAL_AMOUNT") {
        setUnusualMessage(e.message);
        return;
      }
      // Error NO-UNUSUAL después de un confirm: el confirm dialog está abierto
      // y bloqueando visualmente al form. Lo cerramos para que el usuario vea
      // el mensaje de error en el form principal y pueda reaccionar.
      if (confirmUnusual) setUnusualMessage(null);
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await submitWith(false);
  }

  const pending = mutation.isPending;

  // B67: validación SUAVE de cédula — warning amarillo, NUNCA bloquea el
  // submit (proveedores informales sin cédula válida existen).
  const cedulaWarning = supplierId.trim() !== "" && !validateCedulaCR(supplierId).valid;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar costo</DialogTitle>
            <DialogDescription>
              El costo se prorratea entre los animales del lote por días de permanencia en el
              período.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cost-lot" className="font-medium text-[13px] text-app-ink-2">
                Lote
              </label>
              <Select value={lotId} onValueChange={setLotId} disabled={pending}>
                <SelectTrigger
                  id="cost-lot"
                  className="h-10 w-full text-app-ink-1"
                  aria-label="Lote"
                >
                  <SelectValue placeholder="Selecciona un lote" />
                </SelectTrigger>
                <SelectContent>
                  {lots.length === 0 ? (
                    <div className="px-3 py-2 text-[13px] text-app-ink-3">
                      No hay lotes activos.
                    </div>
                  ) : (
                    lots.map((lot) => (
                      <SelectItem key={lot.id} value={lot.id}>
                        {lot.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="cost-category" className="font-medium text-[13px] text-app-ink-2">
                Categoría
              </label>
              <CategorySelect
                id="cost-category"
                value={category}
                onChange={setCategory}
                disabled={pending}
                aria-label="Categoría"
                placeholder="Selecciona una categoría"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="cost-amount" className="font-medium text-[13px] text-app-ink-2">
                Monto
              </label>
              <AmountInput
                id="cost-amount"
                value={amount}
                onChange={setAmount}
                disabled={pending}
                aria-label="Monto en colones"
              />
              <span className="text-[12px] text-app-ink-3">Solo CRC en esta versión.</span>
            </div>

            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-1.5">
                <label
                  htmlFor="cost-period-start"
                  className="font-medium text-[13px] text-app-ink-2"
                >
                  Período desde
                </label>
                <DatePicker
                  id="cost-period-start"
                  value={periodStart}
                  onChange={setPeriodStart}
                  maxDate={periodEnd ?? today}
                  disabled={pending}
                  aria-label="Período desde"
                />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <label htmlFor="cost-period-end" className="font-medium text-[13px] text-app-ink-2">
                  Período hasta
                </label>
                <DatePicker
                  id="cost-period-end"
                  value={periodEnd}
                  onChange={setPeriodEnd}
                  minDate={periodStart}
                  maxDate={today}
                  disabled={pending}
                  aria-label="Período hasta"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="cost-payment-method"
                className="font-medium text-[13px] text-app-ink-2"
              >
                Forma de pago
              </label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as "cash" | "credit")}
                disabled={pending}
              >
                <SelectTrigger
                  id="cost-payment-method"
                  className="h-10 w-full text-app-ink-1"
                  aria-label="Forma de pago"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Crédito</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="cost-occurred-at" className="font-medium text-[13px] text-app-ink-2">
                Fecha del registro
              </label>
              <DatePicker
                id="cost-occurred-at"
                value={occurredAt}
                onChange={setOccurredAt}
                maxDate={today}
                disabled={pending}
                aria-label="Fecha del registro"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="cost-description" className="font-medium text-[13px] text-app-ink-2">
                Descripción
              </label>
              <textarea
                id="cost-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                rows={3}
                disabled={pending}
                placeholder="Ej. Concentrado lácteo La Cerveza, 30 sacos"
                className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
              />
            </div>

            {/* B67 (facturas-v1 §7): sección colapsable de datos de factura.
                Opcional — el flujo rápido de carga no se enlentece. */}
            <div className="rounded-lg border border-app-border">
              <button
                type="button"
                onClick={() => setInvoiceOpen((v) => !v)}
                aria-expanded={invoiceOpen}
                aria-controls="cost-invoice-section"
                disabled={pending}
                className="flex h-10 w-full items-center justify-between px-3 font-medium text-[13px] text-app-ink-2 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Datos de factura <span className="font-normal text-app-ink-3">(opcional)</span>
                <ChevronDown
                  className={`h-4 w-4 text-app-ink-3 transition-transform ${invoiceOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>

              {invoiceOpen && (
                <div
                  id="cost-invoice-section"
                  className="flex flex-col gap-4 border-app-border border-t p-3"
                >
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="cost-invoice-number"
                      className="font-medium text-[13px] text-app-ink-2"
                    >
                      # Factura
                    </label>
                    <input
                      id="cost-invoice-number"
                      type="text"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      maxLength={50}
                      disabled={pending}
                      placeholder="Consecutivo FEL o # de tiquete"
                      className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <label
                        htmlFor="cost-doc-type"
                        className="font-medium text-[13px] text-app-ink-2"
                      >
                        Tipo de documento
                      </label>
                      <Select
                        value={docType}
                        onValueChange={(v) => setDocType(v as InvoiceDocType)}
                        disabled={pending}
                      >
                        <SelectTrigger
                          id="cost-doc-type"
                          className="h-10 w-full text-app-ink-1"
                          aria-label="Tipo de documento"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="factura_electronica">Factura electrónica</SelectItem>
                          <SelectItem value="tiquete">Tiquete</SelectItem>
                          <SelectItem value="recibo">Recibo</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <label
                        htmlFor="cost-invoice-date"
                        className="font-medium text-[13px] text-app-ink-2"
                      >
                        Fecha de emisión
                      </label>
                      <DatePicker
                        id="cost-invoice-date"
                        value={invoiceDate}
                        onChange={setInvoiceDate}
                        maxDate={today}
                        disabled={pending}
                        aria-label="Fecha de emisión de la factura"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="cost-supplier-name"
                      className="font-medium text-[13px] text-app-ink-2"
                    >
                      Proveedor
                    </label>
                    <input
                      id="cost-supplier-name"
                      type="text"
                      value={supplierName}
                      onChange={(e) => setSupplierName(e.target.value)}
                      maxLength={200}
                      disabled={pending}
                      placeholder="Nombre del proveedor"
                      className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="cost-supplier-id"
                      className="font-medium text-[13px] text-app-ink-2"
                    >
                      Cédula del proveedor
                    </label>
                    <input
                      id="cost-supplier-id"
                      type="text"
                      inputMode="numeric"
                      value={supplierId}
                      onChange={(e) => setSupplierId(e.target.value)}
                      maxLength={20}
                      disabled={pending}
                      placeholder="Física (9 dígitos) o jurídica (3-...)"
                      className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
                    />
                    {cedulaWarning && (
                      <output className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                        No parece una cédula CR válida — verificá. Se puede guardar igual.
                      </output>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <label
                        htmlFor="cost-iva-amount"
                        className="font-medium text-[13px] text-app-ink-2"
                      >
                        IVA (monto)
                      </label>
                      <AmountInput
                        id="cost-iva-amount"
                        value={ivaAmount}
                        onChange={setIvaAmount}
                        disabled={pending}
                        aria-label="Monto de IVA en colones"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <label
                        htmlFor="cost-iva-rate"
                        className="font-medium text-[13px] text-app-ink-2"
                      >
                        Tarifa IVA
                      </label>
                      <Select
                        value={ivaRate}
                        onValueChange={(v) => setIvaRate(v as IvaRate)}
                        disabled={pending}
                      >
                        <SelectTrigger
                          id="cost-iva-rate"
                          className="h-10 w-full text-app-ink-1"
                          aria-label="Tarifa IVA"
                        >
                          <SelectValue placeholder="Sin dato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exento">Exento</SelectItem>
                          <SelectItem value="1">1%</SelectItem>
                          <SelectItem value="2">2%</SelectItem>
                          <SelectItem value="4">4%</SelectItem>
                          <SelectItem value="8">8%</SelectItem>
                          <SelectItem value="13">13%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
              >
                {error}
              </p>
            )}

            <DialogFooter>
              <DialogClose asChild>
                <button
                  type="button"
                  disabled={pending}
                  className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
                >
                  Cancelar
                </button>
              </DialogClose>
              <button
                type="submit"
                disabled={pending || formIncomplete}
                className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
              >
                {pending ? "Registrando…" : "Registrar costo"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <UnusualAmountConfirmDialog
        open={unusualMessage !== null}
        onOpenChange={(o) => {
          if (!o) setUnusualMessage(null);
        }}
        onConfirm={() => void submitWith(true)}
        message={unusualMessage ?? ""}
        pending={pending}
      />
    </>
  );
}
